import React, { useEffect, useState, FC } from "react";
import { Row, Col, Typography, Table, Tooltip, Button } from "antd";
import { useIntl } from "react-intl";
import EditSalaryLineModal from "./EditSalaryLineModal";
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import SchedulingModal from "./SchedulingModal";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadSalaryLines, deleteRecord } from "../salarySlice";
import { isAllowedFeature } from "../../organization/organizationSlice";
import moment, { Moment } from "moment";
import RestrictedComponent from "../../../components/util/RestrictedComponent";
import _ from "lodash";
import {
  RootState,
  SalaryTableProps,
  ExtendedSalaryExportLine,
  SalaryPage,
  ColumnSorterFn,
} from "../types";
import { ColumnType } from "antd/es/table";
import { SalaryExportLine } from "../../../types/salary";

const { Text } = Typography;

interface RouteParams {
  branchId: string;
}

/**
 * SalaryTable Component
 *
 * Displays a table of salary entries for a specific employee. Provides actions for
 * managing salary lines including editing, deleting, and viewing scheduling information.
 *
 * Features:
 * - Display salary symbol details including number, name, amounts, and client
 * - Group related salary entries when they share the same symbol
 * - Display retroactive period information
 * - Edit or delete manually entered salary lines (when in edit mode)
 * - View scheduling information related to specific orders
 * - Sorting capabilities on multiple columns
 *
 * @param {SalaryTableProps} props - Component properties
 * @param {SalaryMode} props.mode - 'view' or 'edit' mode
 * @param {EmployeeRecord} props.employee - Employee data object
 * @param {Moment} props.date - Date to filter salary data
 *
 * @example
 * ```tsx
 * <SalaryTable
 *   mode="edit"
 *   employee={selectedEmployee}
 *   date={moment()}
 * />
 * ```
 */
const SalaryTable: FC<SalaryTableProps> = ({ mode, employee, date }) => {
  const intl = useIntl();
  const dispatch = useDispatch<any>();
  const { branchId } = useParams<RouteParams>();
  const location = useLocation();
  const [tableData, setTableData] = useState<ExtendedSalaryExportLine[]>([]);
  const [retroMonth, setRetroMonth] = useState<Moment | null>(null);
  const [selectedSalaryLine, setSelectedSalaryLine] =
    useState<ExtendedSalaryExportLine | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [schedulingOrderId, setSchedulingOrderId] = useState<number | null>(
    null
  );

  // Redux selectors
  const activeBranch = useSelector(
    (state: RootState) => state.salary.activeBranch
  );
  const { salaryLines: salaryPage } = useSelector(
    (state: RootState) => state.salary
  );
  const { items } = salaryPage as SalaryPage;
  const isLoading = useSelector(
    (state: RootState) =>
      state.pendingActions["salaryLine/load"] ||
      state.pendingActions["salaryLine/update"] ||
      state.pendingActions["salaryLine/delete"]
  );
  const isAdmin = location.pathname.includes("admin");

  /**
   * Process and transform raw salary line data into a grouped, hierarchical structure
   * for display in the table. Groups entries by symbol_id and adds aggregated values.
   */
  useEffect(() => {
    if (!items?.length) {
      setTableData([]);
      return;
    }

    const processedItems = items.map((item) => ({
      ...item,
      sum: item?.symbol?.type !== 1 ? calculateSum(item) : null,
      quantity: item?.symbol?.type === 1 ? item.unit_count : null,
    }));

    const grouped = _.groupBy(processedItems, "symbol_id");
    const finalData = Object.values(grouped).map((group) => {
      if (group.length === 1) return group[0];

      const firstItem = group[0];
      return {
        ...firstItem,
        accounting_tag: getConsistentValue<string | null | undefined>(
          group,
          "accounting_tag"
        ),
        order: {
          id: null,
          client: {
            name: getConsistentValue<string | undefined>(
              group,
              (item) => item.order?.client?.name
            ),
          },
        },
        retro_year: getConsistentValue<number | null>(group, "retro_year"),
        retro_month: getConsistentValue<number | null>(group, "retro_month"),
        unit_value: getConsistentValue<number | null | undefined>(
          group,
          "unit_value"
        ),
        children: group.map((child, i) => ({
          ...child,
          collapsed: true,
          key: i,
        })),
        quantity: sumField(group, "quantity"),
        sum: sumField(group, "sum"),
        id: `${firstItem.order?.id || ""}${firstItem.symbol_id}`,
      };
    });

    setTableData(finalData as ExtendedSalaryExportLine[]);
  }, [items]);

  /**
   * Calculate the sum value for a salary line based on unit_count and unit_value
   *
   * @param {SalaryExportLine} item - The salary line to calculate sum for
   * @returns {number} - The calculated sum, rounded to 2 decimal places
   */
  const calculateSum = (item: SalaryExportLine): number => {
    let res = item.unit_count;
    if (
      item.unit_value !== null &&
      item.unit_value !== undefined &&
      item.unit_value !== 0
    ) {
      res = item.unit_value * item.unit_count;
    }
    return Math.round(res * 100) / 100;
  };

  /**
   * Gets a consistent value from a collection of items for a specific field.
   * Returns the value if all items have the same value, otherwise returns "-".
   *
   * @template T - The type of the value to return
   * @param {ExtendedSalaryExportLine[]} items - Collection of salary lines
   * @param {string | ((item: ExtendedSalaryExportLine) => T | undefined)} fieldOrFn - Field name or accessor function
   * @returns {T | string} - The consistent value or "-" if values differ
   */
  const getConsistentValue = <T extends unknown>(
    items: ExtendedSalaryExportLine[],
    fieldOrFn: string | ((item: ExtendedSalaryExportLine) => T | undefined)
  ): T | string => {
    const getValue =
      typeof fieldOrFn === "string"
        ? (item: ExtendedSalaryExportLine) =>
            item[fieldOrFn as keyof ExtendedSalaryExportLine] as unknown as T
        : fieldOrFn;

    const first = getValue(items[0]);
    return items.every((item) => getValue(item) === first) ? (first as T) : "-";
  };

  /**
   * Sums a specific numeric field across multiple salary line items
   *
   * @param {ExtendedSalaryExportLine[]} items - Collection of salary lines
   * @param {keyof Pick<ExtendedSalaryExportLine, "quantity" | "sum">} field - Field to sum
   * @returns {number | null} - Sum of values or null if sum is zero
   */
  const sumField = (
    items: ExtendedSalaryExportLine[],
    field: keyof Pick<ExtendedSalaryExportLine, "quantity" | "sum">
  ): number | null => {
    const sum = items.reduce(
      (acc, item) => acc + ((item[field] as number) || 0),
      0
    );
    return sum !== 0 ? sum : null;
  };

  /**
   * Handles deletion of a salary line.
   * Dispatches delete action and reloads salary lines on success.
   *
   * @param {ExtendedSalaryExportLine} record - The salary line to delete
   */
  const handleDelete = async (record: ExtendedSalaryExportLine) => {
    const params = {
      branchId: isAdmin ? activeBranch : branchId,
      employeeId: record.employee_id,
      id: record.id,
    };
    const action = await dispatch(deleteRecord(params));
    if (!action.error) {
      dispatch(loadSalaryLines(isAdmin, record.employee_id));
    }
  };

  /**
   * Toggles visibility of the edit modal and sets the selected salary line
   *
   * @param {ExtendedSalaryExportLine | null} record - Salary line to edit, or null to close modal
   */
  const toggleEditModal = (record: ExtendedSalaryExportLine | null = null) => {
    setSelectedSalaryLine(record);
    setIsEditModalVisible((prev) => !prev);
  };

  /**
   * Toggles visibility of the scheduling modal and sets the related data
   *
   * @param {ExtendedSalaryExportLine | null} record - Salary line to show scheduling for, or null to close modal
   */
  const toggleSchedulingModal = (
    record: ExtendedSalaryExportLine | null = null
  ) => {
    if (record) {
      const retroDate =
        record.retro_year && typeof record.retro_year === "number"
          ? moment()
              .year(record.retro_year)
              .month((record.retro_month || 1) - 1)
          : null;
      setRetroMonth(retroDate);
      setSchedulingOrderId(record.order?.id || null);
    } else {
      setSchedulingOrderId(null);
      setRetroMonth(null);
    }
  };

  /**
   * Builds and returns the table columns configuration
   *
   * @returns {ColumnType<ExtendedSalaryExportLine>[]} - Array of column definitions
   */
  const getColumns = (): ColumnType<ExtendedSalaryExportLine>[] => {
    const baseColumns: ColumnType<ExtendedSalaryExportLine>[] = [
      {
        title: intl.formatMessage({ id: "app.salary.symbol_number" }),
        dataIndex: "symbol_number",
        render: (_: any, record: ExtendedSalaryExportLine) => (
          <Text>{record?.symbol?.symbol}</Text>
        ),
        sorter: ((a: ExtendedSalaryExportLine, b: ExtendedSalaryExportLine) =>
          Number(a?.symbol?.symbol || 0) -
          Number(b?.symbol?.symbol || 0)) as ColumnSorterFn,
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "ascend" as const,
        width: "70px",
        fixed: "left" as const,
      },
      {
        title: intl.formatMessage({ id: "app.salary.symbol_name" }),
        render: (_: any, record: ExtendedSalaryExportLine) => (
          <Text>{record?.symbol?.name}</Text>
        ),
        sorter: ((a: ExtendedSalaryExportLine, b: ExtendedSalaryExportLine) =>
          (a?.symbol?.name || "").localeCompare(
            b?.symbol?.name || ""
          )) as ColumnSorterFn,
        sortDirections: ["ascend", "descend"],
        width: "120px",
        fixed: "left" as const,
      },
      {
        title: intl.formatMessage({ id: "app.salary.sum" }),
        dataIndex: "sum",
        render: (text: number | null) => <Text>{text}</Text>,
        sorter: ((a: ExtendedSalaryExportLine, b: ExtendedSalaryExportLine) =>
          (a.sum || 0) - (b.sum || 0)) as ColumnSorterFn,
        sortDirections: ["ascend", "descend"],
        width: "70px",
      },
      {
        title: intl.formatMessage({ id: "app.salary.quantity" }),
        dataIndex: "quantity",
        render: (text: number | null) => (
          <Text>{text ? Math.round(text * 100) / 100 : null}</Text>
        ),
        sorter: ((a: ExtendedSalaryExportLine, b: ExtendedSalaryExportLine) =>
          (a.quantity || 0) - (b.quantity || 0)) as ColumnSorterFn,
        sortDirections: ["ascend", "descend"],
        width: "70px",
      },
      {
        title: intl.formatMessage({ id: "app.salary.unit_value" }),
        dataIndex: "unit_value",
        sorter: ((a: ExtendedSalaryExportLine, b: ExtendedSalaryExportLine) =>
          (a.unit_value || 0) - (b.unit_value || 0)) as ColumnSorterFn,
        sortDirections: ["ascend", "descend"],
        width: "70px",
      },
      {
        title: intl.formatMessage({ id: "app.salary.retro_date" }),
        dataIndex: "retro_month",
        render: (_: any, record: ExtendedSalaryExportLine) => {
          console.log(record);
          return (
            <Text>
              {(typeof record.retro_year === "string" &&
                record.retro_year === "-") ||
              (typeof record.retro_month === "string" &&
                record.retro_month === "-")
                ? "-"
                : record.retro_year && typeof record.retro_year === "number"
                ? moment()
                    .year(record.retro_year)
                    .month((record.retro_month || 1) - 1)
                    .format("MM/YYYY")
                : null}
            </Text>
          );
        },
        width: "80px",
      },
      {
        title: intl.formatMessage({ id: "app.salary.client_name" }),
        render: (_: any, record: ExtendedSalaryExportLine) => (
          <Text>{record.order?.client?.name || null}</Text>
        ),
        sorter: ((a: ExtendedSalaryExportLine, b: ExtendedSalaryExportLine) => {
          const aName = a.order?.client?.name || "";
          const bName = b.order?.client?.name || "";
          return aName.localeCompare(bName);
        }) as ColumnSorterFn,
        sortDirections: ["ascend", "descend"],
        width: "150px",
      },
      {
        title: intl.formatMessage({ id: "app.salary.actions" }),
        responsive: ["md"],
        width: "100px",
        fixed: "right" as const,
        render: (_: any, record: ExtendedSalaryExportLine) => (
          <RestrictedComponent
            resource="branch_salary"
            action="editor"
            comp={null}
          >
            <div style={{ display: "flex", gap: 8 }}>
              {record.order?.id && (
                <Tooltip
                  title={intl.formatMessage({
                    id: "app.salary.show_scheduling",
                  })}
                >
                  <Button
                    type="text"
                    icon={<CalendarOutlined />}
                    onClick={() => toggleSchedulingModal(record)}
                  />
                </Tooltip>
              )}
              {record.manual &&
                mode === "edit" &&
                !record.children &&
                !record.symbol?.calculated_only && (
                  <Tooltip
                    title={intl.formatMessage({
                      id: "app.salary.edit_salary_line",
                    })}
                  >
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => toggleEditModal(record)}
                    />
                  </Tooltip>
                )}
              {record.manual && mode === "edit" && !record.children && (
                <Tooltip
                  title={intl.formatMessage({
                    id: "app.salary.delete_salary_line",
                  })}
                >
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              )}
            </div>
          </RestrictedComponent>
        ),
      },
    ];

    if (isAllowedFeature("accounting_tag")) {
      baseColumns.unshift({
        title: intl.formatMessage({ id: "app.salary.accounting_tag" }),
        dataIndex: "accounting_tag",
        render: (text: string | null) => <Text>{text}</Text>,
        width: "110px",
        fixed: "left" as const,
      });
    }

    return baseColumns;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Row>
        <Col span={24}>
          <Table
            size="small"
            bordered
            rowKey={(record) => record.id.toString()}
            scroll={{ y: window.innerHeight * 0.95 - 440 }}
            dataSource={tableData}
            pagination={false}
            columns={getColumns()}
            loading={isLoading}
            rowClassName={(record: ExtendedSalaryExportLine) =>
              record.children
                ? "salary_summary"
                : record.collapsed
                ? "salary_collapsed"
                : ""
            }
          />
        </Col>
      </Row>
      {selectedSalaryLine && (
        <EditSalaryLineModal
          salaryLine={selectedSalaryLine}
          visible={isEditModalVisible}
          onHide={() => toggleEditModal()}
        />
      )}
      <SchedulingModal
        orderId={schedulingOrderId}
        retroMonth={retroMonth}
        employee={employee}
        onHide={() => toggleSchedulingModal()}
        date={date}
      />
    </div>
  );
};

export default SalaryTable;
